import { AsyncPipe, NgClass, TitleCasePipe } from "@angular/common";
import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import { WorkoutInterval } from "app/modules/browse/models/workout.interface";
import { TimerPipe } from "app/modules/dashboard/pipes/timer.pipe";
import { ChartConfiguration, Chart } from "chart.js";
import { BehaviorSubject } from "rxjs";

@Component({
    selector: 'app-workout-chart',
    styleUrls: ['./workout-chart.component.scss'],
    standalone: true,
    imports: [NgClass, TitleCasePipe, AsyncPipe, TitleCasePipe],
    template: `
    <div [ngClass]="classes">
      <div id="chart-header">
        <div class="title">{{ title | titlecase }}</div>
        <div class="data">{{ progress | async }} {{ unit | titlecase }}</div>
      </div>
      <div id="workout-chart-container">
        <canvas #WorkoutChartRef>{{ chart }}</canvas>
      </div>
    </div>`,
})

export class WorkoutChartComponent {
    @ViewChild('WorkoutChartRef') chartCanvas!: ElementRef<HTMLCanvasElement>;

    @Input() workoutIntervals: WorkoutInterval[] = [];
    @Input() duration: number = 0;
    @Input() unit: string = '%'
    @Input() size: 'small' | 'medium' | 'large' = 'medium'
    
    title: string = 'Workout'
    chart: any;
    progress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    timerPipe: TimerPipe = new TimerPipe();
    get classes(): string[] {
        return [`workout-chart-${this.size}`]
    }

    prepareWorkoutIntervalValues(): number[] {
        const data: number[] = this.workoutIntervals.map((int: WorkoutInterval) => int.value);
        return data
    }

    createChart(): void {
        const ctx = this.chartCanvas.nativeElement.getContext('2d');
        if (!ctx) return

        const dynamicBarWidth = {
            id: 'dynamicBarWidth',
            beforeDraw: (chart: any, args: any, options: any) => {
                const { ctx, chartArea: { left, right, top, bottom, width, height }, scales: { x, y } } = chart;

                chart.data.datasets.forEach((dataset: any, i: number) => {
                    const meta = chart.getDatasetMeta(i);
                    let xPosition = left;
                    this.workoutIntervals.forEach((interval: WorkoutInterval, index: number) => {
                        const barWidth = (interval.time / this.duration) * width;
                        const barHeight = height * (interval.value / y.max);
                        meta.data[index].x = xPosition + barWidth / 2;
                        meta.data[index].y = bottom - barHeight;
                        meta.data[index].base = bottom;
                        meta.data[index].width = barWidth;
                        meta.data[index].height = barHeight;
                        xPosition += barWidth;
                    });
                });
            }
        };



        const config: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: this.workoutIntervals.map((int: WorkoutInterval) => int.order),
                datasets: [{
                    data: this.prepareWorkoutIntervalValues(),
                    backgroundColor: this.workoutIntervals.map((int: WorkoutInterval) => int.color),
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'linear',
                        min: 0,
                        max: this.duration,
                        display: true,
                        grid: {
                            display: false,
                        },
                        ticks: {
                            maxRotation:0,
                            callback: (value) => this.timerPipe.transform(value as number),
                            stepSize: Math.max(30, Math.floor(this.duration / 10)), // Ajustez selon vos besoins
                            font: {
                                family: "'Sk-Modernist-Bold'",
                                size: 14
                            }
                        }
                    },
                    y: {
                        beginAtZero: true,
                        // border: {
                        //   display: false
                        // },
                        grid: {
                            tickWidth: 0,
                        },
                        ticks: {
                            crossAlign: 'center',
                            padding: 8,
                            font: {
                                family: "'Sk-Modernist-Bold'",
                                size: 14
                            }
                        }
                    }
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 10,
                        bottom: 0
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        xAlign: 'center',
                        yAlign: 'bottom',
                        caretSize: 6,
                        bodyColor: '#7e8184',
                        callbacks: {
                            title: (tooltipItems) => {
                                const item = tooltipItems[0];
                                const interval: WorkoutInterval = this.workoutIntervals[item.dataIndex]
                                return `${interval.name}`
                            },

                            beforeBody: (tooltipItems) => {
                                const item = tooltipItems[0];
                                const interval: WorkoutInterval = this.workoutIntervals[item.dataIndex]
                                return `Durée: ${this.timerPipe.transform(interval.time)}`;
                            }
                        }
                    }
                }
            },

            plugins: [dynamicBarWidth]
        };

        this.chart = new Chart(ctx, config);
    }

    ngOnInit(): void {
        if (this.workoutIntervals && this.workoutIntervals.length === 0) return;
        if (this.duration === 0) {
            this.duration = this.workoutIntervals.reduce((total, interval) => total + interval.time, 0);
        }

        setTimeout(() => this.createChart(), 0);
    }
}